// ============================================================
// Footer.jsx (Enhanced Legal & Compliance Edition)
// ============================================================
// Junior-dev Notes:
// - Logic: Expanded Privacy/Terms to meet professional standards.
// - UI: Added scrollable Box in Modals for long legal text.
// - Compliance: Content now covers GDPR basics and proctoring ethics.
// ============================================================

import React from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    IconButton,
    Container,
    Grid,
    Divider,
    Link as MuiLink,
    Stack,
    Chip
} from "@mui/material";
import { styled } from "@mui/material/styles";

/* Icons */
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import TerminalIcon from "@mui/icons-material/Terminal";
import GavelIcon from "@mui/icons-material/Gavel";
import MemoryIcon from "@mui/icons-material/Memory";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StorageIcon from "@mui/icons-material/Storage";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_DEEP = "#100720";   // Deep Purple
const FINLAND_BLUE = "#003580";
const TEXT_MUTED = "rgba(255, 255, 255, 0.7)";

/* ── Styled Components ───────────────────────────────────── */

const FooterContainer = styled("footer")(({ theme }) => ({
    width: "100%",
    backgroundColor: NAVY_DEEP,
    color: "#FFFFFF",
    padding: "80px 0 40px 0",
    borderTop: `2px solid ${DOCKER_BLUE}`,
}));

const FooterLink = styled(Typography)({
    color: TEXT_MUTED,
    fontSize: "0.85rem",
    cursor: "pointer",
    transition: "0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    "&:hover": {
        color: DOCKER_BLUE,
        transform: "translateX(5px)",
    },
});

const SocialButton = styled(IconButton)({
    color: "#FFFFFF",
    backgroundColor: "rgba(255,255,255,0.05)",
    "&:hover": {
        backgroundColor: DOCKER_BLUE,
        color: NAVY_DEEP,
    },
});

const FinnishBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "4px",
    border: `1px solid rgba(255,255,255,0.1)`,
    backgroundColor: "rgba(255,255,255,0.03)",
    marginTop: "20px"
});

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 600,
    bgcolor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
    outline: "none",
};

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const [modalContent, setModalContent] = React.useState(null);

    return (
        <FooterContainer>
            <Container maxWidth="lg">
                <Grid container spacing={6} sx={{ mb: 8 }}>

                    {/* 1. CORE MISSION & IDENTITY */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                            ProDev<span style={{ color: DOCKER_BLUE }}>Consult</span>
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                            <Chip
                                icon={<GavelIcon style={{ fontSize: '0.9rem', color: DOCKER_BLUE }} />}
                                label="Secure Examination"
                                size="small"
                                sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                            />
                            <Chip
                                icon={<RecordVoiceOverIcon style={{ fontSize: '0.9rem', color: DOCKER_BLUE }} />}
                                label="Voice Assisted"
                                size="small"
                                sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                            />
                        </Stack>
                        <Typography variant="body2" sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 2 }}>
                            Technical mentorship hub specializing in SaaS Architecture,
                            IoT Simulation, and Embedded Firmware development.
                            Built for rigorous technical validation and interview readiness.
                        </Typography>

                        <FinnishBadge>
                            <Box sx={{ width: 18, height: 12, position: 'relative', bgcolor: '#fff' }}>
                                <Box sx={{ position: 'absolute', top: '40%', width: '100%', height: '20%', bgcolor: FINLAND_BLUE }} />
                                <Box sx={{ position: 'absolute', left: '30%', width: '15%', height: '100%', bgcolor: FINLAND_BLUE }} />
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 1, color: "#fff", opacity: 0.8 }}>
                                FINNISH ENGINEERING STANDARDS
                            </Typography>
                        </FinnishBadge>
                    </Grid>

                    {/* 2. SERVICES & CONSULTATION */}
                    <Grid item xs={6} md={2.5}>
                        <Typography variant="subtitle2" sx={{ color: DOCKER_BLUE, fontWeight: 800, mb: 3 }}>
                            SERVICES
                        </Typography>
                        <FooterLink variant="body2"><StorageIcon sx={{ fontSize: 16 }} /> SaaS Solutions</FooterLink>
                        <FooterLink variant="body2"><MemoryIcon sx={{ fontSize: 16 }} /> Embedded Firmware</FooterLink>
                        <FooterLink variant="body2"><SettingsInputComponentIcon sx={{ fontSize: 16 }} /> IoT Simulation</FooterLink>
                        <FooterLink variant="body2"><TerminalIcon sx={{ fontSize: 16 }} /> Software Consultation</FooterLink>
                    </Grid>

                    {/* 3. TRAINING & LABS */}
                    <Grid item xs={6} md={2.5}>
                        <Typography variant="subtitle2" sx={{ color: DOCKER_BLUE, fontWeight: 800, mb: 3 }}>
                            TRAINING
                        </Typography>
                        <FooterLink variant="body2">Full‑Stack Web Dev</FooterLink>
                        <FooterLink variant="body2">Advanced DSA Lab</FooterLink>
                        <FooterLink variant="body2">Interview Prep</FooterLink>
                        <FooterLink variant="body2">Secure Certification</FooterLink>
                    </Grid>

                    {/* 4. CONTACT */}
                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" sx={{ color: DOCKER_BLUE, fontWeight: 800, mb: 3 }}>
                            GET IN TOUCH
                        </Typography>
                        <FooterLink variant="body2"><EmailIcon sx={{ fontSize: 16 }} /> support@prodevconsult.com</FooterLink>
                        <FooterLink variant="body2"><LocationOnIcon sx={{ fontSize: 16 }} /> Satakunta, Finland</FooterLink>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <SocialButton size="small"><LinkedInIcon fontSize="small" /></SocialButton>
                            <SocialButton size="small"><YouTubeIcon fontSize="small" /></SocialButton>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 4 }} />

                {/* BOTTOM BAR */}
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>
                        © {currentYear} PRODEV CONSULT OY. VALIDATED ENGINEERING HUB.
                    </Typography>
                    <Stack direction="row" spacing={3}>
                        <MuiLink component="button" onClick={() => setModalContent("privacy")} sx={{ color: TEXT_MUTED, fontSize: "0.7rem", textDecoration: "none", "&:hover": { color: "#fff" } }}>PRIVACY POLICY</MuiLink>
                        <MuiLink component="button" onClick={() => setModalContent("terms")} sx={{ color: TEXT_MUTED, fontSize: "0.7rem", textDecoration: "none", "&:hover": { color: "#fff" } }}>TERMS OF SERVICE</MuiLink>
                    </Stack>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.2)" }}>
                        Engineered by <a href="https://www.mavointech.fi" style={{ color: DOCKER_BLUE, textDecoration: "none" }}>Mavointech.fi</a>
                    </Typography>
                </Box>
            </Container>

            {/* MODALS */}
            <Modal open={Boolean(modalContent)} onClose={() => setModalContent(null)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" fontWeight={900} mb={1} color={NAVY_DEEP}>
                        {modalContent === "privacy" ? "Data Privacy Policy" : "Standard Terms & Conditions"}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ maxHeight: "400px", overflowY: "auto", pr: 1, mb: 3 }}>
                        {modalContent === "privacy" ? (
                            <>
                                <Typography variant="subtitle2" color="primary" sx={{ mt: 2, fontWeight: 700 }}>1. Data Collection</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    We collect information necessary for certification and account management, including your name, email, and technical performance metrics.
                                </Typography>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>2. Proctoring & Security</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    To maintain the integrity of our Finnish Engineering Standard, our system monitors session focus, browser visibility, and full-screen adherence during examinations.
                                </Typography>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>3. GDPR Compliance</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Your data is stored securely and processed in accordance with EU data protection laws. You retain the right to request data deletion or access at any time via support@prodevconsult.com.
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="subtitle2" color="primary" sx={{ mt: 2, fontWeight: 700 }}>1. Certification Validity</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Certificates are issued only upon reaching the 70% proficiency threshold. Any attempt to circumvent proctoring software will result in immediate disqualification.
                                </Typography>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>2. Intellectual Property</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    All curriculum content, simulation engines, and algorithm preparatory materials are the exclusive property of ProDev Consult Oy. Unauthorized distribution is prohibited.
                                </Typography>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>3. Limitation of Liability</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    ProDev Consult provides tools for career readiness; however, we do not guarantee specific employment outcomes. Training is provided "as is" without warranties.
                                </Typography>
                            </>
                        )}
                    </Box>
                    <Button
                        fullWidth
                        onClick={() => setModalContent(null)}
                        sx={{
                            bgcolor: NAVY_DEEP,
                            color: "#fff",
                            fontWeight: 700,
                            "&:hover": { bgcolor: DOCKER_BLUE, color: NAVY_DEEP }
                        }}
                    >
                        Accept & Close
                    </Button>
                </Box>
            </Modal>
        </FooterContainer>
    );
}
